/**
 * Cross-tab auth — keeping every open tab on the same session.
 *
 * Dana works the AR report in tabs, so several of them are live at once against
 * one session. `/api/auth/refresh` proxies GoTrue, which ROTATES: the token a
 * tab presents is spent, and a new one comes back. The persist middleware
 * writes that rotation to storage and to the memory of the tab that ran it —
 * and to no other tab. Every sibling goes on holding a token that is now dead.
 *
 * `lib/api.ts` already survives that by reading the refresh token out of
 * storage at exchange time, so a stale in-memory copy is never the thing
 * presented. This listener closes the gap from the other side: it hands each
 * change to the sibling tabs as it happens. Three kinds arrive, told apart by
 * what they do to the key:
 *
 *   - a rotation or a sign-in REPLACES the envelope with a live session;
 *   - an idle expiry replaces it with a signed-out one that says 'idle';
 *   - an explicit logout REMOVES the key.
 *
 * The `storage` event fires only in OTHER documents on the origin, never in the
 * one that wrote — so a tab can never react to its own write.
 */
import {
  useAuthStore,
  persistedAuthFrom,
  AUTH_STORAGE_KEY,
  type PersistedAuth,
} from '@/stores/authStore'
import { hardNavigate } from '@/lib/hardNavigate'
import type { User } from '@/types'

/** Where a tab goes when its session ends elsewhere. */
const LOGIN_PATH = '/login'

/** The part of a StorageEvent this cares about — the rest is untestable noise. */
export type AuthStorageEvent = Pick<StorageEvent, 'key' | 'newValue'>

/** A storage value that is actually usable as a token. */
function isToken(value: unknown): value is string {
  return typeof value === 'string' && value !== ''
}

/** Enough of a user to sign a tab in as — anything less is not adopted. */
function isUser(value: unknown): value is User {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { user_id?: unknown }).user_id === 'string'
  )
}

/**
 * Apply a session change a sibling tab just wrote.
 *
 * A signed-in tab takes the tokens only, never `user`. A rotation changes what
 * this tab may present, not who it belongs to, and quietly swapping an
 * identity under a rendered page is a much worse failure than a stale name.
 *
 * A signed-out tab has no identity to swap — it is sitting on the login page,
 * after a logout or an idle expiry — so when a sibling signs in it takes the
 * whole session, and the login page finishes whatever trip it captured. That
 * is what makes it one login per browser: after idleness signs every tab out,
 * signing in once brings them all back. If someone ELSE signs in, the same
 * holds with their session; a captured destination is only a path, and their
 * own permissions decide what it shows. Only a complete session is adopted —
 * never a user without both tokens, or tokens without a user.
 */
export function handleAuthStorageEvent(event: AuthStorageEvent): void {
  if (event.key !== AUTH_STORAGE_KEY) return

  // A removal is a sibling tab logging out — `logout()` takes the key away
  // rather than emptying it, exactly so this is unambiguous.
  //
  // Leaving via a document load is the whole of it. A router redirect would
  // re-render this tab unauthenticated first, and AppShell/CrewRoute capture
  // the current location on ANY unauthenticated render — so Dana would carry
  // /clients/15210?tab=ar into login state and be sent straight back to it
  // after signing in. Replacing the document means router state never exists
  // and `history.state` is null, so there is nothing to capture: AR-2b's
  // "an explicit logout captures nothing" holds across tabs, by construction.
  //
  // clearAuth() first, so no in-flight request can still present a dead token
  // and no authenticated frame can paint while the load is on its way. Its
  // write does put an empty envelope back in storage; that is inert — it
  // hydrates to exactly the signed-out state an absent key does.
  if (event.newValue === null) {
    useAuthStore.getState().clearAuth()
    hardNavigate(LOGIN_PATH)
    return
  }

  let persisted: Partial<PersistedAuth> | null
  try {
    persisted = persistedAuthFrom(JSON.parse(event.newValue))
  } catch {
    // Another build's shape, or a write truncated by a full quota. Holding the
    // token we have beats adopting nonsense.
    return
  }
  if (!persisted) return

  const current = useAuthStore.getState()
  const signedOut = !current.accessToken && !current.user

  // A sibling ended the session for idleness. Sign out through the ROUTER, not
  // a document load: the store goes unauthenticated, this tab's guard captures
  // where Dana was, and signing back in resumes there — exactly as in the tab
  // that expired. This is why idle expiry writes an envelope instead of
  // removing the key: a removal would land in the branch above and lose it.
  if (persisted.signOutReason === 'idle') {
    if (!signedOut) current.expireForIdle()
    return
  }

  const { user, accessToken, refreshToken } = persisted
  if (!isToken(accessToken) || !isToken(refreshToken)) return

  if (signedOut) {
    if (!isUser(user)) return
    useAuthStore.setState({ user, accessToken, refreshToken, signOutReason: null })
    return
  }

  if (current.accessToken === accessToken && current.refreshToken === refreshToken) {
    // Nothing moved. Returning early matters: setState writes back through the
    // persist middleware, and a write is how this event got here.
    return
  }

  useAuthStore.setState({ accessToken, refreshToken })
}

/**
 * Start listening for sibling tabs' writes. Returns a disposer.
 *
 * Called once from `main.tsx` rather than from a component: the subscription
 * belongs to the tab, not to any tree that happens to be mounted, and it must
 * not come and go with a route transition.
 */
export function startAuthSync(): () => void {
  const listener = (event: StorageEvent) => handleAuthStorageEvent(event)
  window.addEventListener('storage', listener)
  return () => window.removeEventListener('storage', listener)
}
